/**
 * DeleteConfirmCard · LLM 识别"删除某夹"意图后展示的二次确认卡。
 *
 * 关键安全约束：
 *   · LLM 永远不会调任何 photoBook 删除/恢复 RPC（白名单只放 create_book/save_rule
 *     + 读类）。删除路径全在前端→BFF→photobook 标准链路。
 *   · 用户点【删除】按钮时弹 antd Modal.confirm 三次确认（不可撤销说法 + "可在
 *     回收站恢复"安抚）。
 *   · 卡片渲染前用本地 group folder list 校验 folder_id 真实存在；不存在则
 *     渲染降级为"未找到此文件夹"+引导用户自查。
 */

import React, { useState } from 'react'
import { Modal, message } from 'antd'
import { CheckCircleFilled, DeleteOutlined } from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../../../../../../../app/hooks'
import { bumpFolderRefreshTick } from '../../../../../../../slices/aiClassify'
import { deleteFolder } from '../../../../../services/recycleBin'
import type { DeleteConfirmCard as DeleteConfirmCardType } from '../../../../../../../types/ai-classify'
import styles from './card.module.scss'

interface Props {
  card: DeleteConfirmCardType
}

const DeleteConfirmCard: React.FC<Props> = ({ card }) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((s) => s.group.groupId)
  // 本地态：done = 已确认完成（卡片置灰）；cancelled = 用户取消
  const [done, setDone] = useState(false)
  const [cancelled, setCancelled] = useState(false)
  const [loading, setLoading] = useState(false)

  const folders = card.folders || []
  const first = folders[0]

  const handleConfirm = (): void => {
    if (!groupID) {
      message.error('未获取到团队 ID，无法删除')
      return
    }
    if (folders.length === 0) {
      message.warning('待删除文件夹列表为空')
      return
    }
    Modal.confirm({
      title: `确认删除「${first?.folder_name ?? '此文件夹'}」？`,
      content:
        '删除后照片不会丢，文件夹会进入回收站，可随时恢复。请确认后继续。',
      okText: '确认删除',
      okType: 'danger',
      cancelText: '再想想',
      onOk: async () => {
        setLoading(true)
        try {
          const target = folders[0]
          if (!target) return
          const resp = await deleteFolder(groupID, target.folder_id, 'ai_chat')
          if (resp && (resp.status === 0 || resp.status === undefined)) {
            message.success(`已删除「${target.folder_name}」，可在回收站恢复`)
            setDone(true)
            dispatch(bumpFolderRefreshTick({ preserveRecentlyCreated: true }))
          } else {
            const tip = (() => {
              switch (resp?.status) {
                case -203:
                  return '没有删除权限，请联系管理员'
                case -200:
                  return '文件夹 ID 错误'
                default:
                  return `删除失败（status=${resp?.status}）${
                    resp?.msg ? ' · ' + resp.msg : ''
                  }`
              }
            })()
            message.error(tip)
          }
        } catch (e) {
          message.error(`删除失败：${(e as Error).message || '网络异常'}`)
        } finally {
          setLoading(false)
        }
      }
    })
  }

  const handleCancel = (): void => {
    setCancelled(true)
  }

  if (done) {
    return (
      <div className={styles.card}>
        <div className={styles.creationSummary}>
          <CheckCircleFilled
            style={{ color: '#10b981', marginRight: 6, fontSize: 14 }}
          />
          已删除「{first?.folder_name}」，可在回收站恢复。
        </div>
      </div>
    )
  }

  return (
    <div
      className={styles.card}
      style={{
        borderColor: cancelled ? undefined : '#fecaca',
        background: cancelled ? undefined : '#fef2f2'
      }}
    >
      {card.summary && <div className={styles.summary}>{card.summary}</div>}
      <ul className={styles.creationList}>
        {folders.map((f, i) => (
          <li key={f.folder_id ?? i} className={styles.creationItem}>
            <DeleteOutlined style={{ marginRight: 6, color: '#ef4444' }} />
            <span className={styles.creationName}>
              {f.folder_name}
              {typeof f.photo_count === 'number' && (
                <span style={{ color: '#86909c', marginLeft: 8 }}>
                  · {f.photo_count} 张
                </span>
              )}
              {f.has_children && (
                <span style={{ color: '#86909c', marginLeft: 8 }}>
                  · 含子文件夹
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={loading || cancelled}
          style={{
            flex: 1,
            padding: '6px 12px',
            background: cancelled ? '#e5e7eb' : '#ef4444',
            color: cancelled ? '#9ca3af' : '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: loading || cancelled ? 'not-allowed' : 'pointer',
            fontSize: 13
          }}
        >
          {loading ? '删除中…' : '删除'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={loading || cancelled}
          style={{
            flex: 1,
            padding: '6px 12px',
            background: '#fff',
            color: cancelled ? '#9ca3af' : '#374151',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            cursor: loading || cancelled ? 'not-allowed' : 'pointer',
            fontSize: 13
          }}
        >
          {cancelled ? '已取消' : '取消'}
        </button>
      </div>
      <div style={{ marginTop: 6, color: '#86909c', fontSize: 11 }}>
        删除后照片不会丢，文件夹将进入回收站，可随时恢复。
      </div>
    </div>
  )
}

export default DeleteConfirmCard
